// src/components/files/FileExplorer.jsx - Fixed selection logic with auto-scroll
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import FileItem from './FileItem';
import { useFileContext } from '../../contexts/FileContext';
import { formatFileSize, formatDateTime } from '../../services/file.service';

const FileExplorer = ({
  isLoading,
  currentFolder,
  searchQuery,
  viewMode,
  onFileClick,
  onFolderClick,
  onDeleteClick,
  onDownloadClick,
  onAddToInheritanceClick,
  onRenameClick, 
  onCreateFolderClick,
  onUploadFilesClick,
  inheritanceStatus 
}) => {
  const { selectedItems, toggleItemSelection } = useFileContext();

  const [dragSelection, setDragSelection] = useState({
    isSelecting: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0
  });

  const explorerRef = useRef(null);
  const [dragSelectBox, setDragSelectBox] = useState({ display: 'none' });
  const autoScrollIntervalRef = useRef(null);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!currentFolder || !currentFolder.children) return [];

    if (!searchQuery) {
      return currentFolder.children;
    }

    const searchLower = searchQuery.toLowerCase().trim();

    return currentFolder.children.filter(item => {
      let searchableName = item.name;

      // For files, search without extension
      if (item.type !== 'folder') {
        const lastDotIndex = item.name.lastIndexOf('.');
        if (lastDotIndex !== -1) {
          searchableName = item.name.substring(0, lastDotIndex);
        }
      }

      return searchableName.toLowerCase().includes(searchLower);
    });
  }, [currentFolder, searchQuery]);

  const isItemSelected = (item, itemIndex) => {
    return selectedItems.some(selectedItem => {
      // First try to match by array index if available
      if (selectedItem.arrayIndex !== undefined && item.arrayIndex !== undefined) {
        return selectedItem.arrayIndex === item.arrayIndex;
      }
      
      // Fallback to original logic for items without index
      if (item.path && selectedItem.path) {
        return item.path === selectedItem.path;
      }
      
      if (item.id && selectedItem.id) {
        return item.id === selectedItem.id;
      }
      
      return selectedItem.name === item.name && selectedItem.type === item.type;
    });
  };

  const getItemPath = (item, currentPath) => {
    // If the item already has a full path, use it
    if (item.path) {
      return item.path;
    }

    // If we're in a folder, prefix the item name with the current path
    if (currentPath && currentPath.length > 0) {
      // Convert array path to string if needed
      const pathString = Array.isArray(currentPath)
        ? currentPath.join('/')
        : currentPath;

      // Combine path with item name
      return `${pathString}/${item.name}`;
    }

    // Otherwise just use the name
    return item.name;
  };

  // Auto-scroll function for page scrolling with proper interval management
  const handleAutoScroll = useCallback((mouseY) => {
    const scrollThreshold = 100; // pixels from edge to trigger scroll
    const scrollSpeed = 15; // pixels per scroll step
    const viewportHeight = window.innerHeight;

    // Check if mouse is near top or bottom of viewport
    const distanceFromTop = mouseY;
    const distanceFromBottom = viewportHeight - mouseY;

    const shouldScrollUp = distanceFromTop < scrollThreshold && window.scrollY > 0;
    const shouldScrollDown = distanceFromBottom < scrollThreshold && 
                           window.scrollY < (document.documentElement.scrollHeight - window.innerHeight);

    // If we should not be scrolling, clear any existing interval
    if (!shouldScrollUp && !shouldScrollDown) {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      return;
    }

    // If we already have a scroll interval running, don't create a new one
    if (autoScrollIntervalRef.current) {
      return;
    }

    // Start scrolling
    if (shouldScrollUp) {
      autoScrollIntervalRef.current = setInterval(() => {
        if (window.scrollY <= 0) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
          return;
        }
        window.scrollBy(0, -scrollSpeed);
      }, 16);
    } else if (shouldScrollDown) {
      autoScrollIntervalRef.current = setInterval(() => {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        if (window.scrollY >= maxScroll) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
          return;
        }
        
        const beforeScroll = window.scrollY;
        
        // Try multiple scroll methods to force it to work
        const success1 = window.scrollBy(0, scrollSpeed);
        const success2 = window.scrollTo(0, beforeScroll + scrollSpeed);
        
        // Force scroll using requestAnimationFrame
        requestAnimationFrame(() => {
          window.scrollBy(0, scrollSpeed);
          document.documentElement.scrollTop = beforeScroll + scrollSpeed;
          document.body.scrollTop = beforeScroll + scrollSpeed;
        });
        
        // Also try smooth scroll
        window.scrollTo({
          top: beforeScroll + scrollSpeed,
          behavior: 'instant'
        });
        
        const afterScroll = window.scrollY;
      }, 16);
    }
  }, []);

  const selectItemsInDragArea = useCallback((left, top, width, height) => {
    const selectionRect = {
      left,
      top,
      right: left + width,
      bottom: top + height
    };

    // Get all file item elements - more specific selector
    const itemElements = explorerRef.current?.querySelectorAll('.file-item, [data-type]');
    if (!itemElements) return;

    // Convert to array and iterate with proper indexing
    Array.from(itemElements).forEach((element, elementIndex) => {
      const rect = element.getBoundingClientRect();
      const containerRect = explorerRef.current.getBoundingClientRect();
      
      const itemRect = {
        left: rect.left - containerRect.left,
        top: rect.top - containerRect.top,
        right: rect.right - containerRect.left,
        bottom: rect.bottom - containerRect.top
      };

      // Check if item intersects with selection rectangle
      const intersects = !(itemRect.right < selectionRect.left || 
                          itemRect.left > selectionRect.right || 
                          itemRect.bottom < selectionRect.top || 
                          itemRect.top > selectionRect.bottom);

      if (intersects) {
        const itemName = element.getAttribute('data-name');
        const itemType = element.getAttribute('data-type');
        
        const filteredItemIndex = filteredItems.findIndex((item, index) => {
          return item.name === itemName && item.type === itemType;
        });
        
        if (filteredItemIndex !== -1) {
          const filteredItem = filteredItems[filteredItemIndex];
          const enrichedItem = {
            ...filteredItem,
            path: filteredItem.path || getItemPath(filteredItem, currentFolder?.path || ''),
            arrayIndex: filteredItemIndex // Use the actual index from filtered items
          };

          // Check if item is already selected using the duplicate-aware logic
          const isAlreadySelected = isItemSelected(enrichedItem, filteredItemIndex);

          // Add to selection if not already selected
          if (!isAlreadySelected) {
            toggleItemSelection(enrichedItem);
          }
        }
      }
    });
  }, [filteredItems, selectedItems, currentFolder, toggleItemSelection, isItemSelected, getItemPath]);

const handleMouseDown = useCallback((e) => {
  
  // Don't start drag selection on interactive elements
  if (e.target.closest('.item-checkbox') || 
      e.target.closest('.item-actions') ||
      e.target.closest('button') ||
      e.target.closest('input[type="checkbox"]') ||
      e.target.closest('.dropdown-menu')) {
    return;
  }

  // Don't start drag selection when clicking on file items themselves
  if (e.target.closest('.file-item') || 
      e.target.closest('.item') || 
      e.target.closest('[data-type]') ||
      e.target.closest('.file-item-grid') ||
      e.target.closest('.card-item-container')) {
    return;
  }

  if (e.button !== 0) {
    return;
  }


  const rect = explorerRef.current.getBoundingClientRect();
  const startX = e.clientX - rect.left;
  const startY = e.clientY - rect.top;


  // Clear existing selection if not holding Ctrl/Cmd (only when clicking on empty space)
  if (!e.ctrlKey && !e.metaKey && selectedItems.length > 0) {
    selectedItems.forEach(item => toggleItemSelection(item));
  }

  setDragSelection({
    isSelecting: true,
    startX,
    startY,
    currentX: startX,
    currentY: startY,
    addToSelection: e.ctrlKey || e.metaKey
  });

  setDragSelectBox({
    display: 'block',
    left: startX,
    top: startY,
    width: 0,
    height: 0
  });

  e.preventDefault();
}, [selectedItems, toggleItemSelection]);

  // Handle mouse move during drag selection
  const handleMouseMove = useCallback((e) => {
    if (!dragSelection.isSelecting) return;

    const rect = explorerRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    const left = Math.min(dragSelection.startX, currentX);
    const top = Math.min(dragSelection.startY, currentY);
    const width = Math.abs(currentX - dragSelection.startX);
    const height = Math.abs(currentY - dragSelection.startY);

    setDragSelection(prev => ({
      ...prev,
      currentX,
      currentY
    }));

    setDragSelectBox({
      display: 'block',
      left,
      top,
      width,
      height
    });

    // Handle auto-scrolling (pass global mouse Y coordinate for container edge detection)
    handleAutoScroll(e.clientY);

    // Select items within the drag area
    selectItemsInDragArea(left, top, width, height);
  }, [dragSelection.isSelecting, dragSelection.startX, dragSelection.startY, selectItemsInDragArea, handleAutoScroll]);

  // Handle mouse up to end drag selection
  const handleMouseUp = useCallback(() => {
    if (!dragSelection.isSelecting) return;

    // Clear auto-scroll
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    setDragSelection({
      isSelecting: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0
    });

    setDragSelectBox({ display: 'none' });
  }, [dragSelection.isSelecting]);

  // Add mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = (e) => handleMouseUp(e);

    if (dragSelection.isSelecting) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      // Clean up auto-scroll on unmount
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [dragSelection.isSelecting, handleMouseMove, handleMouseUp]);

  // Handle item click
  const handleItemClick = (item, event) => {
    // Prevent if clicking on checkbox or actions
    if (
      event.target.closest('.item-checkbox') ||
      event.target.closest('.item-actions')
    ) {
      return;
    }

    if (item.type === 'folder') {
      onFolderClick(item.path || item.name);
    } else {
      onFileClick(item);
    }
  };

  const handleCheckboxChange = (item, isChecked, itemIndex) => {
    // Ensure the item has array index for unique identification
    const enrichedItem = {
      ...item,
      path: getItemPath(item, currentFolder?.path || ''),
      arrayIndex: itemIndex // Critical for distinguishing duplicates
    };
    
    toggleItemSelection(enrichedItem);
    
    // Enhanced logging
    setTimeout(() => {
      console.table(selectedItems.map((item, index) => ({
        selectionIndex: index + 1,
        arrayIndex: item.arrayIndex,
        name: item.name,
        type: item.type,
        path: item.path || 'No path',
        size: item.size ? formatFileSize(item.size) : 'N/A'
      })));
    }, 100);
  };

  // Render empty state
  const renderEmptyState = () => (
    <div className="empty-state text-center py-5">
      <i className="fas fa-folder-open fa-3x text-black mb-3"></i>
      <h5 className="text-black">No Files Found</h5>
      <p className="text-black">This folder is empty. Upload files or create a new folder.</p>
    </div>
  );

  // Render loading state
  const renderLoading = () => (
    <div className="text-center py-5">
      <div className="spinner-border" role="status" style={{ color: '#00CC5C' }}>
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );

  // Render items
  const renderItems = () => {
    if (isLoading) {
      return renderLoading();
    }

    if (filteredItems.length === 0) {
      return renderEmptyState();
    }

    return (
      <div className={`items-wrapper ${viewMode}-view`}>
        {filteredItems
          .filter(item => item.name !== 'user_profile.jpg')
          .map((item, arrayIndex) => {
            // Enrich item with array index for unique identification
            const enrichedItem = {
              ...item,
              path: item.path || getItemPath(item, currentFolder?.path || ''),
              arrayIndex: arrayIndex // Add array index to distinguish duplicates
            };

            return (
              <FileItem
                key={`${arrayIndex}-${item.name}-${item.type}`} // Use index in key
                item={enrichedItem}
                itemIndex={arrayIndex} // Pass index to FileItem
                viewMode={viewMode}
                isSelected={isItemSelected(enrichedItem, arrayIndex)}
                onItemClick={handleItemClick}
                onCheckboxChange={(item, checked) => handleCheckboxChange(item, checked, arrayIndex)}
                onDeleteClick={() => onDeleteClick(enrichedItem)}
                onDownloadClick={() => onDownloadClick(enrichedItem)}
                onAddToInheritanceClick={() => onAddToInheritanceClick(enrichedItem)}
                onRenameClick={() => onRenameClick(item)}
                inheritanceStatus={inheritanceStatus}
              />
            );
          })}
      </div>
    );
  };

  return (
    <div 
      id="fileExplorer" 
      className="file-explorer"
      ref={explorerRef}
      onMouseDown={handleMouseDown}
      style={{ 
        position: 'relative', 
        userSelect: 'none'
      }}
    >
      {renderItems()}
      
      {/* Drag selection box */}
      <div
        className="drag-select-box"
        style={{
          position: 'absolute',
          border: '1px dashed #007bff',
          backgroundColor: 'rgba(0, 123, 255, 0.1)',
          pointerEvents: 'none',
          zIndex: 1000,
          ...dragSelectBox
        }}
      />
    </div>
  );
};

FileExplorer.propTypes = {
  isLoading: PropTypes.bool,
  currentFolder: PropTypes.object,
  searchQuery: PropTypes.string,
  viewMode: PropTypes.oneOf(['list', 'grid']),
  onFileClick: PropTypes.func.isRequired,
  onFolderClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onDownloadClick: PropTypes.func.isRequired,
  onAddToInheritanceClick: PropTypes.func.isRequired,
  onRenameClick: PropTypes.func.isRequired,
  onCreateFolderClick: PropTypes.func.isRequired,
  onUploadFilesClick: PropTypes.func.isRequired,
  inheritanceStatus: PropTypes.object
};

FileExplorer.defaultProps = {
  isLoading: false,
  currentFolder: { name: 'All Files', type: 'folder', children: [] },
  searchQuery: '',
  viewMode: 'list',
  inheritanceStatus: {}
};

export default FileExplorer;